import { customElement } from "lit/decorators.js";
import { PropertyValues } from "lit";
import { SugarElement } from "../configurator/SugarCanvas";
import { Engine, Entity } from "../engine/Engine";
import { AccumulatingGTAOPass } from "../engine/postprocesses/AccumulatingGTAOPass";
import { GTAOPass } from "../engine/postprocesses/GTAOPass";
import { CameraTracker } from "../engine/camera/CameraTracker";
import { logger } from "../util/Logger";

/**
 * GTAO post-process that temporally accumulates ambient occlusion across
 * frames. The pass is reset whenever the scene or camera changes so stale AO
 * from a previous view is never shown.
 *
 * Attributes:
 *   - `max-samples` (number, default 256): frames to accumulate before the
 *     pass stops requesting further redraws.
 *   - `denoise` (boolean, default false): when set, Poisson-denoises the
 *     accumulated AO before blending onto the scene.
 */
class GTAOAccumulatePostProcessEntity extends Entity {
  private _gtaoPass?: AccumulatingGTAOPass;
  private _maxSamples = 256;
  private _denoise = false;
  private _tracker?: CameraTracker;
  /** Last logged frame progress, so we only print every ~16 frames. */
  private _lastLoggedFrame = -1;

  private _pauseAccumulation = (reason: string): void => {
    if (!this._gtaoPass) return;
    this._gtaoPass.reset();
    this._gtaoPass.setAccumulate(false);
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _resumeAccumulation = (reason: string): void => {
    if (!this._gtaoPass) return;

    this._gtaoPass.reset();
    this._gtaoPass.setAccumulate(true);
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _interactionStartHandler = () => this._pauseAccumulation("interaction-start");
  private _interactionEndHandler = () => this._resumeAccumulation("interaction-end");

  private _syncGtaoSceneAndCamera(): void {
    const eng = this.engine;
    if (!this._gtaoPass || !eng?.activeCamera) return;
    this._gtaoPass.scene = eng.getSceneForWebGLRender();
    this._gtaoPass.camera = eng.activeCamera.instance;
  }

  private _onWebglRenderSceneChanged = () => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
  };

  /** Sahne içeriği (mesh/material/transform) — Engine SceneMutationObserver → scene-mutated */
  private _onSceneChanged = () => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
    //this.engine?.dispatchCustomEvent("request-update");
  };

  private _onUpdateCamera = (_e?: unknown) => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
  };

  private _onEngineResize = () => {
    this._gtaoPass?.reset();
  };

  private _onContinueAccumulation = () => {
    const pass = this._gtaoPass;
    if (pass) {
      const f = pass.frameIndex;
      const max = pass.maxSamples;
    }
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _addGTAOPass(): void {
    if (this._gtaoPass) return;

    const aoParameters = {
      radius: .25,
      distanceExponent: 1,
      thickness: 1.5,
      scale: 0.75,
      samples: 2,
      distanceFallOff: 0.5,
      screenSpaceRadius: true,
      output: GTAOPass.OUTPUT.AO,
    };
    const pdParameters = {
      lumaPhi: 10,
      depthPhi: 2,
      normalPhi: 3,
      radius: 2,
      radiusExponent: 1,
      rings: 2,
      samples: 32,
      active: this._denoise,
    };

    this._gtaoPass = new AccumulatingGTAOPass(
      this.engine!.getSceneForWebGLRender(),
      this.engine!.activeCamera!.instance,
      1024,
      1024,
      { maxSamples: this._maxSamples, denoise: this._denoise },
    );
    this._gtaoPass.updateGtaoMaterial(aoParameters);
    this._gtaoPass.updatePdMaterial(pdParameters);
    this._gtaoPass._onContinueAccumulation = this._onContinueAccumulation;

    this.engine?.removePass(this._gtaoPass);
    this.engine?.addPass(this._gtaoPass);

    this.engine?.addEventListener("update-camera", this._onUpdateCamera);
    this.engine?.addEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    this.engine?.addEventListener(
      "interaction-start",
      this._interactionStartHandler,
    );
    this.engine?.addEventListener(
      "interaction-end",
      this._interactionEndHandler,
    );
    this.engine?.addEventListener("engine-resize", this._onEngineResize);
    this.engine?.addEventListener("scene-mutated", this._onSceneChanged);

    // High-level camera motion signal. The tracker consolidates camera-start
    // / camera-change / camera-end into clean motion transitions, filtering
    // out SmoothOrbitController's per-frame `camera-change` noise. During
    // motion we additionally reset each tick so nothing accumulates while
    // the view is still drifting.
    this._tracker = new CameraTracker({
      engine: this.engine!,
      label: "GTAO",
      onMotionStart: () => this._pauseAccumulation("motion-start"),
      onMotionChange: () => this._gtaoPass?.reset(),
      onMotionEnd: () => this._resumeAccumulation("motion-end"),
    });
  }

  public setMaxSamples(n: number): void {
    this._maxSamples = n;
    this._gtaoPass?.setMaxSamples(n);
    this._lastLoggedFrame = -1;
    this.engine?.dispatchCustomEvent("request-update");
  }

  public setDenoise(enabled: boolean): void {
    this._denoise = enabled;
    this._gtaoPass?.setDenoise(enabled);
    this.engine?.dispatchCustomEvent("request-update");
  }

  onAdd(_engine: Engine): void {
    this._addGTAOPass();
  }

  onRemove(engine: Engine): void {
    this._tracker?.dispose();
    this._tracker = undefined;

    engine.removePass(this._gtaoPass);
    engine.removeEventListener("update-camera", this._onUpdateCamera);
    engine.removeEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    engine.removeEventListener(
      "interaction-start",
      this._interactionStartHandler,
    );
    engine.removeEventListener("interaction-end", this._interactionEndHandler);
    engine.removeEventListener("engine-resize", this._onEngineResize);
    engine.removeEventListener("scene-mutated", this._onSceneChanged);
    this._gtaoPass?.dispose();
    this._gtaoPass = undefined;
  }
}

@customElement("gtao-accumulate-postprocess")
export class GTAOAccumulatePostProcessElement extends SugarElement {
  static override properties = {
    ...SugarElement.properties,
    maxSamples: { type: Number, attribute: "max-samples" },
    denoise: { type: Boolean },
  };

  maxSamples = 256;
  denoise = false;

  createEntity(): Entity | undefined {
    return new GTAOAccumulatePostProcessEntity();
  }

  /**
   * Lit calls `update()` before `firstUpdated()`, i.e. before `createEntity()`
   * runs, so attribute-driven changes on the very first render hit an
   * undefined entity and are dropped. Push the current property values into
   * the entity right after it's created so the pass is constructed with the
   * user-provided `max-samples` / `denoise` instead of the defaults.
   */
  override firstUpdated(e: unknown): void {
    super.firstUpdated(e as never);
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    if (!ent) return;
    ent.setMaxSamples(this.maxSamples);
    ent.setDenoise(this.denoise);
  }

  protected override update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    if (!ent) return;
    if (changedProperties.has("maxSamples")) {
      ent.setMaxSamples(this.maxSamples);
    }
    if (changedProperties.has("denoise")) {
      ent.setDenoise(this.denoise);
    }
  }
}
